import { useState, useEffect, useCallback, useRef } from 'react';
import { asyncDataDiagnosisService, JobStatus } from '../services/asyncDataDiagnosisService';
import { authManager } from '../services/authManager';
import { USE_BACKEND, BYPASS_AUTH } from '../config/env';

// Progress persistence utilities
const PROGRESS_STORAGE_KEY = 'dataObject_progress';

interface PersistedProgress {
    dataObjectUuid: string;
    jobId: string;
    progress: number;
    phase: string;
    status: string;
    timestamp: number;
}

const saveProgress = (dataObjectUuid: string, jobStatus: JobStatus) => {
    try {
        const saved = JSON.parse(localStorage.getItem(PROGRESS_STORAGE_KEY) || '{}');
        saved[dataObjectUuid] = {
            dataObjectUuid,
            jobId: jobStatus.id,
            progress: jobStatus.progress,
            phase: jobStatus.current_phase,
            status: jobStatus.status,
            timestamp: Date.now()
        };
        localStorage.setItem(PROGRESS_STORAGE_KEY, JSON.stringify(saved));
    } catch (error) {
        console.warn('Failed to save progress to localStorage:', error);
    }
};

const getPersistedProgress = (dataObjectUuid: string): PersistedProgress | null => {
    try {
        const saved = JSON.parse(localStorage.getItem(PROGRESS_STORAGE_KEY) || '{}');
        const progress = saved[dataObjectUuid];
        if (progress && Date.now() - progress.timestamp < 24 * 60 * 60 * 1000) { // 24 hours
            return progress;
        }
        return null;
    } catch (error) {
        console.warn('Failed to read progress from localStorage:', error);
        return null;
    }
};

const clearProgress = (dataObjectUuid: string) => {
    try {
        const saved = JSON.parse(localStorage.getItem(PROGRESS_STORAGE_KEY) || '{}');
        delete saved[dataObjectUuid];
        localStorage.setItem(PROGRESS_STORAGE_KEY, JSON.stringify(saved));
    } catch (error) {
        console.warn('Failed to clear progress from localStorage:', error);
    }
};

interface UseRunningJobsOptions {
    autoRefresh?: boolean;
    refreshInterval?: number;
    isAuthenticated?: boolean;
    // New: Only poll for specific data objects
    watchedDataObjects?: string[]; // Array of UUIDs to watch
}

interface UseRunningJobsResult {
    runningJobs: JobStatus[];
    loading: boolean;
    error: string | null;
    refresh: () => void;
    getJobForDataObject: (dataObjectUuid: string) => JobStatus | undefined;
    // New: Manage watched objects
    addWatchedObject: (dataObjectUuid: string) => void;
    removeWatchedObject: (dataObjectUuid: string) => void;
    getPersistedProgressForObject: (dataObjectUuid: string) => PersistedProgress | null;
    // Smart polling additions
    pollingMode: 'active' | 'idle';
    recordActivity: () => void;
}

/**
 * Hook for managing running/pending jobs
 * Fetches active jobs and auto-refreshes periodically
 */
export const useRunningJobs = (options: UseRunningJobsOptions = {}): UseRunningJobsResult => {
    const {
        autoRefresh = true,
        refreshInterval = 8000, // Increased from 5s to 8s to reduce API calls
        isAuthenticated = false,
        watchedDataObjects = []
    } = options;

    const [runningJobs, setRunningJobs] = useState<JobStatus[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [watchedObjects, setWatchedObjects] = useState<Set<string>>(new Set(watchedDataObjects));
    const [isPageVisible, setIsPageVisible] = useState(true);
    const [lastActivityTime, setLastActivityTime] = useState<number>(Date.now());
    const [pollingMode, setPollingMode] = useState<'active' | 'discovery' | 'idle'>('discovery');
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    // Dynamic intervals based on actual activity
    const ACTIVE_INTERVAL = 5000;     // 5s when jobs are actually running
    const IDLE_CHECK_INTERVAL = 0;    // No discovery polling - only poll when needed

    // Page visibility detection
    useEffect(() => {
        const handleVisibilityChange = () => {
            setIsPageVisible(!document.hidden);
        };
        
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, []);

    // Determine if we should poll based on actual running processes
    const shouldPoll = useCallback(() => {
        // Only poll if we have watched objects that are likely still running
        return watchedObjects.size > 0;
    }, [watchedObjects.size]);

    // Record activity (user action or new jobs detected)
    const recordActivity = useCallback(() => {
        setLastActivityTime(Date.now());
    }, []);

    const fetchRunningJobs = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            // Skip fetching if backend is enabled but user is not authenticated (unless bypass is enabled)
            if (USE_BACKEND && !BYPASS_AUTH && !authManager.isAuthenticated()) {
                setLoading(false);
                return;
            }

            const jobs = await asyncDataDiagnosisService.getRunningJobs();
            
            // If we found jobs and weren't expecting them, record activity
            if (jobs.length > 0 && runningJobs.length === 0) {
                recordActivity();
            }
            
            // Filter to only watched objects since we only poll when we have specific objects to track
            let relevantJobs: JobStatus[];
            if (watchedObjects.size > 0) {
                relevantJobs = jobs.filter(job => {
                    if (!job.data_object_id) return false;
                    const isWatched = watchedObjects.has(job.data_object_id);
                    return isWatched;
                });
            } else {
                // No watched objects - return empty (we shouldn't be polling in this case)
                relevantJobs = [];
            }
            
            // Save progress for each relevant job
            relevantJobs.forEach(job => {
                if (job.data_object_id) {
                    saveProgress(job.data_object_id, job);
                    
                    // Remove from watched if job is complete
                    if (['SUCCESS', 'FAILED', 'CANCELLED'].includes(job.status)) {
                        setWatchedObjects(prev => {
                            const newSet = new Set(prev);
                            newSet.delete(job.data_object_id!);
                            return newSet;
                        });
                    }
                }
            });
            
            setRunningJobs(relevantJobs);
            
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch running jobs');
        } finally {
            setLoading(false);
        }
    }, [watchedObjects, recordActivity, runningJobs.length, isPageVisible]);

    const refresh = useCallback(() => {
        fetchRunningJobs();
    }, [fetchRunningJobs]);

    // New functions for managing watched objects with activity tracking
    const addWatchedObject = useCallback((dataObjectUuid: string) => {
        setWatchedObjects(prev => new Set([...prev, dataObjectUuid]));
        recordActivity(); // Record activity when user starts watching an object
    }, [recordActivity]);

    const removeWatchedObject = useCallback((dataObjectUuid: string) => {
        setWatchedObjects(prev => {
            const newSet = new Set(prev);
            newSet.delete(dataObjectUuid);
            return newSet;
        });
        clearProgress(dataObjectUuid);
    }, []);

    const getPersistedProgressForObject = useCallback((dataObjectUuid: string): PersistedProgress | null => {
        return getPersistedProgress(dataObjectUuid);
    }, []);

    // Function to find a job for a specific data object
    const getJobForDataObject = useCallback((dataObjectUuid: string): JobStatus | undefined => {
        const matchingJob = runningJobs.find(job => {
            // Match by data_object_id first (most reliable)
            const uuidMatch = job.data_object_id === dataObjectUuid;

            // Fallback to source_identifier or dataset name matching for backward compatibility
            const sourceMatch = job.source_identifier === dataObjectUuid ||
                job.source_identifier.startsWith(dataObjectUuid + '.') ||
                job.source_identifier.endsWith('/' + dataObjectUuid) ||
                job.source_identifier.endsWith('\\' + dataObjectUuid);

            return uuidMatch || sourceMatch;
        });

        return matchingJob;
    }, [runningJobs]);

    // Strict polling effect - only poll when we actually have processes to track
    useEffect(() => {
        if (!autoRefresh) return;

        // Only fetch if authentication requirements are met
        if (USE_BACKEND && !BYPASS_AUTH && !isAuthenticated) {
            return;
        }

        // Don't poll when page is hidden (save bandwidth/battery)
        if (!isPageVisible) {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            return;
        }

        // ONLY poll if we have objects we're actively watching
        const shouldStartPolling = shouldPoll();
        
        if (!shouldStartPolling) {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            // Clear any stale jobs since we're not watching anything
            setRunningJobs([]);
            return;
        }

        // Initial fetch
        fetchRunningJobs();

        // Set up interval for active polling only
        intervalRef.current = setInterval(fetchRunningJobs, ACTIVE_INTERVAL);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [autoRefresh, isAuthenticated, fetchRunningJobs, isPageVisible, shouldPoll, watchedObjects.size]);

    // Clean up when no more objects to watch
    useEffect(() => {
        if (watchedObjects.size === 0 && runningJobs.length === 0) {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        }
    }, [watchedObjects.size, runningJobs.length]);

    return {
        runningJobs,
        loading,
        error,
        refresh,
        getJobForDataObject,
        addWatchedObject,
        removeWatchedObject,
        getPersistedProgressForObject,
        pollingMode: watchedObjects.size > 0 ? 'active' : 'idle', // Simplified mode
        recordActivity
    };
};